import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth";
import { Avatar } from "../../../../ui/components/Avatar";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { Select } from "../../../../ui/components/Select";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { FeatherHelpCircle, FeatherFileText, FeatherCalendar } from "@subframe/core";

interface MonthOption {
  value: string;
  label: string;
}

// Generate month options (last 24 months)
const generateMonthOptions = (): MonthOption[] => {
  const months: MonthOption[] = [];
  const today = new Date();
  
  for (let i = 0; i < 24; i++) {
    const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear());
    
    months.push({
      value: `${year}-${month}`, // Format: YYYY-MM
      label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) // Format: January 2025
    });
  }
  
  return months;
};

interface IncomeStatementItem {
  label: string;
  amount: number;
  level?: number; // For indentation levels (0 = main, 1 = sub, 2 = sub-sub)
}

interface IncomeStatement {
  revenue: IncomeStatementItem[];
  expenses: IncomeStatementItem[];
  netIncome: number;
}

export const IncomeStatementReportsPage: React.FC = () => {
  const { company } = useAuth();
  const navigate = useNavigate();
  const [fromMonth, setFromMonth] = useState<string>("");
  const [toMonth, setToMonth] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [incomeStatement, setIncomeStatement] = useState<IncomeStatement | null>(null);

  const monthOptions = generateMonthOptions();

  // Generate client ID from company name
  const generateClientId = (companyName?: string): string => {
    if (!companyName) return "CLIENT-2024-001";
    const initials = companyName
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase();
    return `${initials}-2024-001`;
  };

  // Handle raise query button
  const handleRaiseQuery = (): void => {
    navigate("/cfo/dashboard/queries");
  };

  // Handle generate reports
  const handleGenerateReports = async (): Promise<void> => {
    if (!fromMonth || !toMonth) {
      alert("Please select both From and To months");
      return;
    }

    setIsGenerating(true);
    
    // TODO: Replace with actual API call
    // Simulate API call
    setTimeout(() => {
      // Mock data for now
      const mockIncomeStatement: IncomeStatement = {
        revenue: [
          { label: "Sales Revenue", amount: 1500000, level: 0 },
          { label: "Service Revenue", amount: 500000, level: 0 },
          { label: "Other Income", amount: 50000, level: 0 },
        ],
        expenses: [
          { label: "Cost of Goods Sold", amount: 800000, level: 0 },
          { label: "Operating Expenses", amount: 400000, level: 0 },
          { label: "  Salaries and Wages", amount: 250000, level: 1 },
          { label: "  Rent", amount: 100000, level: 1 },
          { label: "  Utilities", amount: 30000, level: 1 },
          { label: "  Other Operating Expenses", amount: 20000, level: 1 },
          { label: "Interest Expense", amount: 15000, level: 0 },
          { label: "Tax Expense", amount: 50000, level: 0 },
        ],
        netIncome: 385000,
      };
      
      setIncomeStatement(mockIncomeStatement);
      setIsGenerating(false);
    }, 1000);
  };

  // Format currency
  const formatCurrency = (amount: number): string => {
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Calculate total for a section
  const calculateTotal = (items: IncomeStatementItem[]): number => {
    return items.reduce((sum, item) => sum + item.amount, 0);
  };

  const totalRevenue = incomeStatement ? calculateTotal(incomeStatement.revenue) : 0;
  const totalExpenses = incomeStatement ? calculateTotal(incomeStatement.expenses) : 0;

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start">
        {/* Common Header Section */}
        <div className="flex w-full flex-col items-start gap-4 border-b border-solid border-neutral-border bg-default-background px-8 py-6">
          <div className="flex w-full flex-wrap items-start justify-between">
            <div className="flex items-center gap-4">
              <Avatar
                size="large"
                image="https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OHx8Y29tcGFueXxlbnwwfHwwfHx8MA%3D%3D"
              />
              <div className="flex flex-col items-start gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    {company?.companyName || "Acme Corporation"}
                  </span>
                  <Badge variant="warning">High Risk</Badge>
                </div>
                <span className="text-body font-body text-subtext-color">
                  Client ID: {generateClientId(company?.companyName)}
                </span>
              </div>
            </div>
            <Button
              variant="neutral-tertiary"
              icon={<FeatherHelpCircle />}
              onClick={handleRaiseQuery}
            >
              Raise Query
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start bg-default-background overflow-auto p-8">
          <div className="flex w-full max-w-6xl flex-col items-start gap-6">
            {/* Page Title */}
            <div className="flex w-full flex-col items-start gap-2">
              <h1 className="text-heading-1 font-heading-1 text-default-font">
                Income Statement Reports
              </h1>
              <p className="text-body font-body text-subtext-color">
                Generate and view income statement reports for the selected period.
              </p>
            </div>

            {/* Month Filter Section */}
            <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background p-6">
              <div className="flex w-full items-center gap-4">
                <div className="flex items-center gap-2">
                  <FeatherCalendar className="text-body font-body text-subtext-color" />
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    Select Period
                  </span>
                </div>
              </div>
              
              <div className="flex w-full items-center gap-4">
                <div className="flex flex-col gap-1" style={{ minWidth: "200px" }}>
                  <Select
                    label="From Month"
                    placeholder="Select from month"
                    value={fromMonth}
                    onValueChange={setFromMonth}
                    icon={<FeatherCalendar />}
                  >
                    {monthOptions.map((option) => (
                      <Select.Item key={option.value} value={option.value}>
                        {option.label}
                      </Select.Item>
                    ))}
                  </Select>
                </div>

                <div className="flex flex-col gap-1" style={{ minWidth: "200px" }}>
                  <Select
                    label="To Month"
                    placeholder="Select to month"
                    value={toMonth}
                    onValueChange={setToMonth}
                    icon={<FeatherCalendar />}
                  >
                    {monthOptions.map((option) => (
                      <Select.Item key={option.value} value={option.value}>
                        {option.label}
                      </Select.Item>
                    ))}
                  </Select>
                </div>

                <div className="flex items-end h-8">
                  <Button
                    variant="brand-primary"
                    icon={<FeatherFileText />}
                    onClick={handleGenerateReports}
                    disabled={isGenerating || !fromMonth || !toMonth}
                  >
                    {isGenerating ? "Generating..." : "Generate Reports"}
                  </Button>
                </div>
              </div>
            </div>

            {/* Income Statement Report */}
            {incomeStatement && (
              <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background p-6">
                <div className="flex w-full items-center justify-between">
                  <h2 className="text-heading-2 font-heading-2 text-default-font">
                    Income Statement
                  </h2>
                  <span className="text-body font-body text-subtext-color">
                    {monthOptions.find(m => m.value === fromMonth)?.label} to {monthOptions.find(m => m.value === toMonth)?.label}
                  </span>
                </div>

                <div className="flex w-full flex-col items-start gap-4">
                  {/* Revenue Section */}
                  <div className="flex w-full flex-col items-start gap-2">
                    <div className="text-heading-3 font-heading-3 text-default-font pb-2 border-b border-solid border-neutral-border w-full">
                      Revenue
                    </div>
                    {incomeStatement.revenue.map((item, index) => (
                      <div
                        key={index}
                        className="flex w-full items-center justify-between"
                        style={{ paddingLeft: `${(item.level || 0) * 24}px` }}
                      >
                        <span className={`text-body font-body ${
                          item.level === 0 ? 'text-default-font' : 'text-subtext-color'
                        }`}>
                          {item.label}
                        </span>
                        <span className="text-body font-body text-default-font">
                          {formatCurrency(item.amount)}
                        </span>
                      </div>
                    ))}
                    <div className="flex w-full items-center justify-between pt-2 border-t border-solid border-neutral-border">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Total Revenue
                      </span>
                      <span className="text-body-bold font-body-bold text-default-font">
                        {formatCurrency(totalRevenue)}
                      </span>
                    </div>
                  </div>

                  {/* Expenses Section */}
                  <div className="flex w-full flex-col items-start gap-2">
                    <div className="text-heading-3 font-heading-3 text-default-font pb-2 border-b border-solid border-neutral-border w-full">
                      Expenses
                    </div>
                    {incomeStatement.expenses.map((item, index) => (
                      <div
                        key={index}
                        className="flex w-full items-center justify-between"
                        style={{ paddingLeft: `${(item.level || 0) * 24}px` }}
                      >
                        <span className={`text-body font-body ${
                          item.level === 0 ? 'text-default-font' : 'text-subtext-color'
                        }`}>
                          {item.label}
                        </span>
                        <span className="text-body font-body text-default-font">
                          {formatCurrency(item.amount)}
                        </span>
                      </div>
                    ))}
                    <div className="flex w-full items-center justify-between pt-2 border-t border-solid border-neutral-border">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Total Expenses
                      </span>
                      <span className="text-body-bold font-body-bold text-default-font">
                        {formatCurrency(totalExpenses)}
                      </span>
                    </div>
                  </div>

                  {/* Net Income */}
                  <div className="flex w-full items-center justify-between pt-4 border-t-2 border-solid border-neutral-border">
                    <span className="text-heading-3 font-heading-3 text-default-font">
                      Net Income
                    </span>
                    <span className="text-heading-3 font-heading-3 text-default-font">
                      {formatCurrency(incomeStatement.netIncome)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DefaultPageLayout>
  );
};

